// FP3-035B-1 — Admin Add Student modal.
//
// Admins create students manually (not via CSV) with the full
// platform record up-front: identity, demographics, eligibility,
// status/due dates, and service areas. Team-assignment architecture
// is deferred to FP3-035B-2; this modal accepts a simple multi-
// select of providers so admins can at least seed the team. The
// backend stores them in `assigned_providers[]` exactly the way
// CSV import does today.
//
// All fields write through the existing POST /api/students endpoint
// (which we extended in FP3-035B-1 to accept sex,
// eligibility_category, iep_due_date, met_due_date).
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, GraduationCap } from "@phosphor-icons/react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { prettyApiError } from "@/lib/api";
import { invalidateAfterMutation } from "@/lib/adminQueries";
import { useAuth } from "@/lib/auth";
import { ATTENDANCE_OPTIONS, SEX_OPTIONS } from "@/lib/studentProfileTaxonomy";
import EligibilityEditor, { primaryEligibilityKey } from "@/components/student/EligibilityEditor";
// FP3-131 · Duplicate-Student-Guard.
import DuplicateStudentGuardDialog from "@/components/students/DuplicateStudentGuardDialog";

const GRADES = Array.from({ length: 13 }, (_, i) => String(i));
const STATUS_OPTIONS = [
  { value: "active", label: "IEP / Active" },
  { value: "rti", label: "RTI" },
  { value: "monitoring", label: "Monitoring" },
  { value: "evaluation", label: "Evaluation" },
  { value: "exited", label: "Exited" },
];

function ageLabel(dob) {
  if (!dob || typeof dob !== "string" || dob.length < 10) return "";
  const [by, bm, bd] = dob.slice(0, 10).split("-").map((n) => parseInt(n, 10));
  if (!by || !bm || !bd) return "";
  const now = new Date();
  let years = now.getFullYear() - by;
  let months = now.getMonth() + 1 - bm;
  if (now.getDate() < bd) months -= 1;
  if (months < 0) { years -= 1; months += 12; }
  if (years < 0) return "";
  return `${years};${months}`;
}

export default function AdminAddStudentModal({ onClose, onCreated, campuses = [] }) {
  // FP3-078 — Authorization checkpoint gate. Resolves immediately
  // for users who have already acknowledged; otherwise opens the
  // StudentDataAckModal and waits for the user to confirm.
  const { ensureStudentDataAck } = useAuth();
  // FP3-043 Phase A follow-up #7 — Campus list MUST come from
  // `/api/schools` (org-scoped) so org admins never see demo
  // campuses. The deprecated `campuses` prop is kept only as a
  // best-effort hint for the first paint; the real source is the
  // schools list we fetch below. Demo fallback ("Lincoln/Westfield")
  // has been removed entirely — empty state is intentional.
  const [schools, setSchools] = useState([]);
  const [schoolsLoaded, setSchoolsLoaded] = useState(false);

  const [draft, setDraft] = useState({
    first_name: "",
    last_name: "",
    grade: "0",
    campus: "",
    school_id: "",
    teacher: "",
    teacher_email: "",
    // FP3-045 — Identity fields. Optional; normalized server-side
    // (student_email lowercased + trimmed; external_id trimmed).
    external_id: "",
    student_email: "",
    attendance_pattern: "full_day",
    status: "active",
    date_of_birth: "",
    sex: "",
    eligibility_categories: [],   // FP3-035B-1 correction — multi-eligibility
    lre: "",                      // FP3-035B-1 follow-up — Least Restrictive Environment
    iep_due_date: "",
    met_due_date: "",
    service_areas: [],
    assigned_providers: [],
  });

  const [taxonomy, setTaxonomy] = useState(null);
  const [eligibilityCategories, setEligibilityCategories] = useState([]);
  const [lreLevels, setLreLevels] = useState([]);
  const [providers, setProviders] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Pull the master service taxonomy (no role filter — admin can
    // assign anything) + eligibility list + LRE list + provider list
    // + org-scoped schools in parallel.
    api.getServicesTaxonomy({}).then((r) => setTaxonomy(r.data)).catch(() => setTaxonomy(null));
    api.getEligibilityCategories()
      .then((r) => setEligibilityCategories(r.data?.categories || []))
      .catch(() => setEligibilityCategories([]));
    api.getLreLevels()
      .then((r) => setLreLevels(r.data?.levels || []))
      .catch(() => setLreLevels([]));
    api.getProviders()
      .then((r) => setProviders(r.data?.providers || r.data || []))
      .catch(() => setProviders([]));
    // FP3-043 Phase A follow-up #7 — Org-scoped schools list.
    // `/api/schools` is auth-gated AND filters by organization_id
    // for non-super-admin; no demo campus ever surfaces here.
    api.listSchools()
      .then((r) => {
        const rows = Array.isArray(r?.data) ? r.data : [];
        const cleaned = rows
          .filter((s) => s && s.id && (s.name || "").trim())
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setSchools(cleaned);
        setSchoolsLoaded(true);
        // Auto-select the only campus if the org has exactly one.
        // Otherwise leave blank so the operator must commit.
        if (cleaned.length === 1) {
          setDraft((d) => ({
            ...d,
            campus: cleaned[0].name,
            school_id: cleaned[0].id,
          }));
        }
      })
      .catch(() => {
        setSchools([]);
        setSchoolsLoaded(true);
      });
  }, []);

  const services = taxonomy?.services || [];

  const toggleService = (svc) =>
    setDraft((d) => {
      const has = d.service_areas.includes(svc.label);
      return {
        ...d,
        service_areas: has
          ? d.service_areas.filter((s) => s !== svc.label)
          : [...d.service_areas, svc.label],
      };
    });

  const toggleProvider = (pid) =>
    setDraft((d) => {
      const has = d.assigned_providers.includes(pid);
      return {
        ...d,
        assigned_providers: has
          ? d.assigned_providers.filter((p) => p !== pid)
          : [...d.assigned_providers, pid],
      };
    });

  const age = useMemo(() => ageLabel(draft.date_of_birth), [draft.date_of_birth]);

  // FP3-131 · Duplicate-Student-Guard state.
  const [guardOpen, setGuardOpen] = useState(false);
  const [guardMatchKind, setGuardMatchKind] = useState("");
  const [guardCandidates, setGuardCandidates] = useState([]);

  const _buildCreatePayload = () => ({
    first_name: draft.first_name.trim(),
    last_name: draft.last_name.trim(),
    grade: draft.grade,
    campus: draft.campus,
    school_id: draft.school_id || null,
    teacher: draft.teacher || draft.teacher_email.split("@")[0],
    teacher_email: draft.teacher_email.trim().toLowerCase(),
    external_id: (draft.external_id || "").trim(),
    student_email: (draft.student_email || "").trim(),
    attendance_pattern: draft.attendance_pattern,
    status: draft.status,
    date_of_birth: draft.date_of_birth || null,
    sex: draft.sex || "",
    eligibility_category: primaryEligibilityKey(draft.eligibility_categories),
    eligibility_categories: (draft.eligibility_categories || []).filter((e) => e.category),
    lre: draft.lre || "",
    iep_due_date: draft.iep_due_date || null,
    met_due_date: draft.met_due_date || null,
    service_areas: draft.service_areas,
    services: services
      .filter((s) => draft.service_areas.includes(s.label))
      .map((s) => ({
        key: s.key,
        label: s.label,
        domain: s.domain,
        categories: s.categories || [],
      })),
    assigned_providers: draft.assigned_providers,
  });

  const _createWith = async (opts = {}) => {
    const payload = _buildCreatePayload();
    const r = await api.createStudent(payload, opts);
    toast.success(`Added ${draft.first_name} ${draft.last_name}`);
    invalidateAfterMutation.studentEdited();
    onCreated?.(r.data);
  };

  const submit = async () => {
    if (!draft.first_name.trim() || !draft.last_name.trim()) {
      toast.error("First and last name are required");
      return;
    }
    if (!draft.teacher_email.trim() || !draft.teacher_email.includes("@")) {
      toast.error("Teacher email is required");
      return;
    }
    // FP3-043 Phase A follow-up #7 — When the org has any campuses,
    // the operator must commit to one. When the org has none, allow
    // create with blank campus (operator already saw the hint).
    if (schools.length > 0 && !draft.school_id) {
      toast.error("Pick a campus before creating the student");
      return;
    }
    setSaving(true);
    try {
      // FP3-078 — Block on the student-data authorization gate
      // before the API write. If the user hasn't acknowledged yet,
      // the modal pops here and resolves only after they confirm
      // (or cancels, in which case the create is abandoned).
      const ok = await ensureStudentDataAck();
      if (!ok) {
        setSaving(false);
        return;
      }
      // FP3-131 · Try the create. On 409 duplicate_student_candidates
      // we show the guard dialog instead of a toast — the caller can
      // still override with `Add anyway` (retries with force=true).
      try {
        await _createWith();
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (
          e?.response?.status === 409 &&
          detail?.code === "duplicate_student_candidates"
        ) {
          setGuardMatchKind(detail.match_kind || "soft");
          setGuardCandidates(detail.candidates || []);
          setGuardOpen(true);
          return;   // keep the modal open, wait for guard resolution
        }
        throw e;
      }
    } catch (e) {
      toast.error(`Failed: ${prettyApiError(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleForceCreate = async () => {
    setSaving(true);
    try {
      await _createWith({ force: true });
      setGuardOpen(false);
    } catch (e) {
      toast.error(`Failed: ${prettyApiError(e)}`);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      data-testid="admin-add-student-modal"
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl flex flex-col max-h-[88vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-[#E5E3DB] flex items-center justify-between shrink-0">
          <h2 className="text-lg font-semibold text-[#1F2937] heading-font inline-flex items-center gap-2">
            <GraduationCap size={18} weight="duotone" className="text-[#9C6B2E]" />
            Add Student
          </h2>
          <button
            onClick={onClose}
            className="text-[#6B7280] hover:text-[#1F2937]"
            data-testid="admin-add-student-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
          {/* Section: Identity */}
          <SectionHeader title="Student identity" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name *">
              <Input
                value={draft.first_name}
                onChange={(e) => setDraft({ ...draft, first_name: e.target.value })}
                data-testid="admin-add-student-first"
                className="h-9 text-sm"
              />
            </Field>
            <Field label="Last name *">
              <Input
                value={draft.last_name}
                onChange={(e) => setDraft({ ...draft, last_name: e.target.value })}
                data-testid="admin-add-student-last"
                className="h-9 text-sm"
              />
            </Field>
            <Field label="Grade">
              <select
                value={draft.grade}
                onChange={(e) => setDraft({ ...draft, grade: e.target.value })}
                data-testid="admin-add-student-grade"
                className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full"
              >
                {GRADES.map((g) => (
                  <option key={g} value={g}>Grade {g}</option>
                ))}
              </select>
            </Field>
            <Field label="Campus">
              <select
                value={draft.school_id}
                onChange={(e) => {
                  const sid = e.target.value;
                  const match = schools.find((s) => s.id === sid);
                  setDraft({
                    ...draft,
                    school_id: sid,
                    campus: match ? match.name : "",
                  });
                }}
                disabled={schoolsLoaded && schools.length === 0}
                data-testid="admin-add-student-campus"
                className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full disabled:bg-[#F3F1EC] disabled:text-[#9B9890]"
              >
                {/* FP3-043 Phase A follow-up #7 — Real campuses only.
                    No demo fallback ("Lincoln/Westfield") — empty
                    state is the correct production behavior for a
                    fresh org. */}
                {schools.length === 0 ? (
                  <option value="">
                    {schoolsLoaded
                      ? "No campuses available — add a school first"
                      : "Loading campuses…"}
                  </option>
                ) : (
                  <>
                    {schools.length > 1 && (
                      <option value="" data-testid="admin-add-student-campus-blank">
                        Select a campus…
                      </option>
                    )}
                    {schools.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </>
                )}
              </select>
              {schoolsLoaded && schools.length === 0 && (
                <p
                  className="text-[10px] text-[#9B9890] italic mt-1"
                  data-testid="admin-add-student-campus-empty-hint"
                >
                  No campuses in this organization yet. Add a school from
                  Schools &amp; Campuses before creating students.
                </p>
              )}
            </Field>
            <Field label="Teacher name">
              <Input
                value={draft.teacher}
                onChange={(e) => setDraft({ ...draft, teacher: e.target.value })}
                data-testid="admin-add-student-teacher"
                className="h-9 text-sm"
                placeholder="e.g. Ms. Patel"
              />
            </Field>
            <Field label="Teacher email *">
              <Input
                type="email"
                value={draft.teacher_email}
                onChange={(e) => setDraft({ ...draft, teacher_email: e.target.value })}
                data-testid="admin-add-student-teacher-email"
                className="h-9 text-sm"
                placeholder="p.patel@school.edu"
              />
            </Field>
            {/* FP3-045 — Student identity. Both fields are optional. */}
            <Field label="External Student ID">
              <Input
                value={draft.external_id}
                onChange={(e) => setDraft({ ...draft, external_id: e.target.value })}
                data-testid="admin-add-student-external-id"
                className="h-9 text-sm"
                placeholder="e.g. SIS-12345"
              />
              <p className="text-[10px] text-[#9B9890] italic mt-1">
                Optional. Use the school or district&apos;s local student ID if available.
              </p>
            </Field>
            <Field label="Student Email">
              <Input
                type="email"
                value={draft.student_email}
                onChange={(e) => setDraft({ ...draft, student_email: e.target.value })}
                data-testid="admin-add-student-student-email"
                className="h-9 text-sm"
                placeholder="student@school.edu"
              />
              <p className="text-[10px] text-[#9B9890] italic mt-1">
                Optional. Used by some schools as a student identifier and for future integrations.
              </p>
            </Field>
            <Field label="Attendance">
              <select
                value={draft.attendance_pattern}
                onChange={(e) => setDraft({ ...draft, attendance_pattern: e.target.value })}
                data-testid="admin-add-student-pattern"
                className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full"
              >
                {ATTENDANCE_OPTIONS.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
            </Field>
          </div>

          {/* Section: Demographics */}
          <SectionHeader title="Demographics" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date of birth">
              <Input
                type="date"
                value={draft.date_of_birth}
                onChange={(e) => setDraft({ ...draft, date_of_birth: e.target.value })}
                data-testid="admin-add-student-dob"
                className="h-9 text-sm"
              />
              {age && (
                <p className="text-[10px] text-[#6B7280] mt-1" data-testid="admin-add-student-age">
                  Age <span className="text-[#1F2937] font-medium">{age}</span> (years;months)
                </p>
              )}
            </Field>
            <Field label="Sex">
              <select
                value={draft.sex}
                onChange={(e) => setDraft({ ...draft, sex: e.target.value })}
                data-testid="admin-add-student-sex"
                className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full"
              >
                {SEX_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </Field>
            {/* FP3-080 — Sex is the source of truth for articulation
                norm comparisons; no separate speech norm field. */}
          </div>

          {/* Section: Special Education */}
          <SectionHeader title="Special education" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Status">
              <select
                value={draft.status}
                onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                data-testid="admin-add-student-status"
                className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
            <Field label="IEP due date">
              <Input
                type="date"
                value={draft.iep_due_date}
                onChange={(e) => setDraft({ ...draft, iep_due_date: e.target.value })}
                data-testid="admin-add-student-iep-due"
                className="h-9 text-sm"
              />
            </Field>
            <Field label="MET / Evaluation due date">
              <Input
                type="date"
                value={draft.met_due_date}
                onChange={(e) => setDraft({ ...draft, met_due_date: e.target.value })}
                data-testid="admin-add-student-met-due"
                className="h-9 text-sm"
              />
            </Field>
            {/* FP3-035B-1 follow-up — LRE level. Canonical taxonomy
                (A/B/C/D/E/H/J); Not specified = empty. */}
            <Field label="LRE (Least Restrictive Environment)">
              <select
                value={draft.lre || ""}
                onChange={(e) => setDraft({ ...draft, lre: e.target.value })}
                data-testid="admin-add-student-lre"
                className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full"
              >
                <option value="">Not specified</option>
                {lreLevels.map((lvl) => (
                  <option key={lvl.key} value={lvl.key}>{lvl.label}</option>
                ))}
              </select>
            </Field>
          </div>
          {/* FP3-035B-1 correction — Multi-eligibility editor.
              Up to 4 entries; primary required when any are set.
              SLD / DD / PSD show subcategory checkboxes. */}
          <div className="space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563]">
              Eligibility categories
            </p>
            <p className="text-[10px] text-[#9B9890] italic">
              Official school category. Up to 4 entries. Separate from service areas.
            </p>
            <EligibilityEditor
              value={draft.eligibility_categories}
              onChange={(next) => setDraft({ ...draft, eligibility_categories: next })}
              taxonomy={eligibilityCategories}
              testIdPrefix="admin-add-student-elig"
            />
          </div>

          {/* Section: Service areas */}
          <SectionHeader title="Service areas" />
          <div data-testid="admin-add-student-services" className="flex flex-wrap gap-1.5">
            {services.length === 0 && (
              <span className="text-[11px] text-[#9B9890] italic">Loading taxonomy…</span>
            )}
            {services.map((svc) => {
              const selected = draft.service_areas.includes(svc.label);
              return (
                <button
                  key={svc.key}
                  type="button"
                  onClick={() => toggleService(svc)}
                  data-testid={`admin-add-student-svc-${svc.key}`}
                  className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-colors ${
                    selected
                      ? "bg-[#1E3F33] text-white"
                      : "bg-[#E1EAE4] text-[#1E3F33] hover:bg-[#d5e5d9]"
                  }`}
                >
                  {svc.label}
                </button>
              );
            })}
          </div>

          {/* Section: Provider assignment (basic) */}
          <SectionHeader
            title="Provider assignment"
            subtitle="Select one or more providers. Full primary-provider / evaluation-team rules ship in FP3-035B-2."
          />
          <div data-testid="admin-add-student-providers" className="space-y-1.5">
            {providers.length === 0 && (
              <p className="text-[11px] text-[#9B9890] italic">No providers loaded.</p>
            )}
            <div className="flex flex-wrap gap-1.5">
              {providers.map((p) => {
                const selected = draft.assigned_providers.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggleProvider(p.id)}
                    data-testid={`admin-add-student-provider-${p.id}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                      selected
                        ? "bg-[#1E3F33] text-white"
                        : "bg-[#F1EFE8] text-[#1F2937] hover:bg-[#E5E3DB]"
                    }`}
                  >
                    {p.first_name || ""} {p.last_name || p.name || ""} <span className="opacity-60">· {p.role || "—"}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-[#E5E3DB] flex justify-end gap-2 bg-[#FAFAF7] shrink-0">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-lg h-9 text-sm border-[#d4d2cc]"
            data-testid="admin-add-student-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="admin-add-student-submit"
            className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg h-9 text-sm"
          >
            {saving ? "Saving…" : "Add Student"}
          </Button>
        </div>
        {/* FP3-131 · Duplicate-Student-Guard */}
        <DuplicateStudentGuardDialog
          open={guardOpen}
          matchKind={guardMatchKind}
          candidates={guardCandidates}
          onCancel={() => setGuardOpen(false)}
          onForceCreate={handleForceCreate}
          submitting={saving}
        />
      </div>
    </div>,
    document.body,
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div>
      <h3 className="text-[11px] uppercase tracking-wider font-semibold text-[#4B5563]">
        {title}
      </h3>
      {subtitle && (
        <p className="text-[10px] text-[#9B9890] italic mt-0.5">{subtitle}</p>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5">
        {label}
      </p>
      {children}
    </div>
  );
}
